import { useEffect, useRef } from "react"
import Select from "@/v2-ui/select"
import { LICENSE_PLAN_NONE_VALUE } from "@/v2-backoffice-client/license/license.constants"
import { useLicensePlansQuery } from "@/v2-backoffice-client/license/plan/license.plan.query"
import type { LicensePlanFragment } from "@/v2-backoffice-client/license/plan/__types__/license.plan.fragment"

export type LicensePlanSelectProps = {
  /** The license id as a string, or LICENSE_PLAN_NONE_VALUE. */
  value: string
  onValueChange(value: string): void
  /**
   * Narrows the list to the catalog licenses of this product. A license
   * set up for no product is never offered: it grants nothing, and under a
   * product it reads as a choice (FUS-2766). Empty = every license.
   */
  productCode?: string
  /**
   * Leaves "None" out: a seated product (the MCP servers) needs a license
   * from the catalog, its packages are what let people in.
   */
  requirePlan?: boolean
}

// The operator plan of a product (OP-802, OP-901, OP-1001: the plans
// lic.Seed_Data owns for the MCP servers, which have no Fenerum plan to
// sell on): the pick when the product has several live licenses.
function isOperatorPlan(plan: LicensePlanFragment) {
  return plan.Code?.startsWith("OP-") ?? false
}

// The Fenerum license catalog as a select, for the grant dialogs: WHAT an
// operator license is a license of — "a trial of Placepoint Professional" —
// which is what gives the grant its package entitlements (the license's feature
// groups). Owns its catalog query like LicenseProductSelect; mount it inside
// the dialog so it only fetches when shown. Deprecated licenses stay pickable
// (an existing deal may be exactly that) but say so. The moment a product is
// chosen the picker fills itself in where there is one right answer: the
// product's only live license, or its operator plan when a Fenerum plan
// sits beside it (the MCP servers); the operator can still change it.
function LicensePlanSelect(props: LicensePlanSelectProps) {
  const { value, onValueChange, productCode, requirePlan } = props

  const { data, loading } = useLicensePlansQuery()
  const plans = (data?.getLicensePlans ?? []).filter(
    (plan) => !productCode || String(plan.ProductCode) === productCode,
  )

  // Once per product choice. Deliberate later changes (None included) are
  // left alone, which is what the ref is for.
  const autoPickedFor = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (!productCode || !data || autoPickedFor.current === productCode) return
    autoPickedFor.current = productCode
    if (value !== LICENSE_PLAN_NONE_VALUE) return
    const live = plans.filter((plan) => !plan.DeprecatedAt)
    const operatorPlans = live.filter(isOperatorPlan)
    const pick =
      live.length === 1
        ? live[0]
        : operatorPlans.length === 1
          ? operatorPlans[0]
          : undefined
    if (pick) onValueChange(String(pick.Id))
  }, [productCode, data, plans, value, onValueChange])

  return (
    <Select value={value} onValueChange={onValueChange}>
      <Select.Trigger size="md" className="w-full">
        <Select.Value
          placeholder={loading ? "Loading licenses..." : "Pick a license..."}
        />
      </Select.Trigger>
      <Select.Content>
        {!requirePlan && (
          <Select.Item value={LICENSE_PLAN_NONE_VALUE}>
            None — the customer gets nothing from packages, only add-ons
          </Select.Item>
        )}
        {plans.map((plan) => (
          <Select.Item key={plan.Id} value={String(plan.Id)}>
            {plan.Name}
            {plan.Code ? ` (${plan.Code})` : ""}
            {plan.DeprecatedAt ? " — deprecated" : ""}
          </Select.Item>
        ))}
      </Select.Content>
    </Select>
  )
}

export default LicensePlanSelect
